import { forwardRef, InputHTMLAttributes, memo } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';

type CheckboxAttributes = InputHTMLAttributes<HTMLInputElement>;

type CheckboxProps = CheckboxAttributes & {
  indeterminate?: boolean;
  label?: string;
};

export const Checkbox = memo(
  forwardRef<HTMLInputElement, CheckboxProps>(
    ({ indeterminate = false, label, className, checked, disabled, ...rest }, ref) => {
      const CheckboxIcon = (
        <Icon
          name={indeterminate ? 'minus' : 'check'}
          size="s16"
          className={cn('check', { checked })}
          aria-hidden="true"
        />
      );

      return (
        <Label className={cn(className, { disabled, checked })}>
          <input
            type="checkbox"
            ref={ref}
            className="checkbox"
            checked={checked}
            disabled={disabled}
            {...rest}
          />
          <span className={cn('checkbox-view', { disabled })}>{CheckboxIcon}</span>
          {label && (
            <span className="label-text">
              <span className="text">{label}</span>
            </span>
          )}
        </Label>
      );
    },
  ),
);

Checkbox.displayName = 'Checkbox';

// ====== styles ======

export const tokens = {
  gap: '8px',
  fontSize: '14px',
  size: '1.143em',
  borderRadius: '4px',
  borderWidth: '2px',
  transition: '150ms ease',
  // colors
  borderDefault: COLORS.systemDark100,
  borderHover: COLORS.systemSky200,
  focusRing: COLORS.systemSky300,
  checkedBg: COLORS.systemSky200,
  checkedHoverBg: COLORS.systemSky300,
  checkedInner: COLORS.systemLight100,
  disabledBg: COLORS.systemDark100,
  disabledBorder: COLORS.systemDark100,
  disabledInner: COLORS.systemDark200,
};

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.gap};
  font-size: ${tokens.fontSize};
  cursor: pointer;
  user-select: none;
  position: relative;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  .label-text {
    cursor: inherit;

    .text {
      position: relative;
      z-index: 1;
    }
  }

  .checkbox {
    position: absolute;
    width: 1px;
    height: 1px;
    left: calc(${tokens.size} / 2);
    border: 0;
    white-space: nowrap;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
  }

  .checkbox:focus-visible + .checkbox-view {
    outline: 2px solid ${tokens.focusRing};
    outline-offset: 2px;
  }

  .checkbox:disabled + .checkbox-view {
    background: ${tokens.disabledBg};
    border-color: ${tokens.disabledBorder};
  }

  .checkbox-view {
    position: relative;
    width: ${tokens.size};
    height: ${tokens.size};
    min-height: ${tokens.size};
    border-radius: ${tokens.borderRadius};
    border: ${tokens.borderWidth} solid ${tokens.borderDefault};
    transition:
      border-color ${tokens.transition},
      background ${tokens.transition};
  }

  &:not(.disabled):hover .checkbox-view {
    border-color: ${tokens.borderHover};
  }

  .checkbox:checked + .checkbox-view:not(.disabled) {
    background: ${tokens.checkedBg};
    border-color: ${tokens.checkedBg};
  }

  .checkbox:checked + .checkbox-view:not(.disabled):hover {
    background: ${tokens.checkedHoverBg};
    border-color: ${tokens.checkedHoverBg};
  }

  .check {
    font-size: inherit;
    color: ${tokens.checkedInner};
    position: absolute;
    transform: translate(-7%, -7%);
  }

  &:not(.disabled) .check.checked {
    opacity: 1;
  }

  &.disabled .check.checked {
    color: ${tokens.disabledInner};
  }

  &.disabled .check:not(.checked) {
    color: ${tokens.disabledBg};
  }
`;
